import { useEffect, useState } from 'react'
import { sendGet } from 'hooks/use-fetch'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { useRefresh } from 'ContextProviders/RefreshProvider'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { CARENETWORK_MANAGEMENT_DETAILS } from 'Pages/Routes'
import { generatePath, useNavigate } from 'react-router'
import 'core/styles.css'
import { ChildInfoLayout, type ChildInfoDetails } from 'Components/ChildDetails/ChildInfoLayout'
import { Lock } from '@mui/icons-material'
import { DuplicateDisclaimer } from './DuplicateDisclaimer'

export interface ChildInfoCardProps {
  onEdit?: () => void
  showViewDetailsLink?: boolean
  pendingDuplicateID?: number | null
}

export function ChildInfoCard (props: ChildInfoCardProps & { id: number }): JSX.Element {
  const nav = useNavigate()
  const [details, setDetails] = useState<ChildInfoDetails | null>(null)
  const refreshTime = useRefresh()
  const account = useAccountId()

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response, success } = await sendGet(`/ChildDetails/Find?id=${props.id}&accountId=${account?.id ?? 0}&accountType=${account?.type ?? ''}&requestingModule=CareNetwork`, {})
      if (success) {
        setDetails(response.response)
      }
    }
    void loadData()
  }, [refreshTime, props.id])

  const redirectToDetails = (): void => {
    const editChild = `${generatePath(CARENETWORK_MANAGEMENT_DETAILS, { id: props.id.toString() })}`
    nav(editChild)
  }

  if (details === null) {
    return <CircularProgress />
  } else {
    const showLocked: boolean = !details.readOnly && props.pendingDuplicateID != null && props.onEdit !== undefined

    return <Box className='CardDisplay' data-testid="ChildInfoCard">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
        <Typography variant='h6' sx={{ fontWeight: 'bold' }} gutterBottom>Child Information </Typography>
        {showLocked && <Lock />}
      </Box>
      <ChildInfoLayout {...details} />

      {(props.showViewDetailsLink ?? false) && <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button data-testid='gotoChild' sx={{ textTransform: 'none', textDecoration: 'underline', color: 'black' }} onClick={redirectToDetails}>See Full Child Record</Button>
      </Box>}
      {!showLocked && !details.readOnly && props.onEdit != null && <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button data-testid='editChild' color='primary' variant='contained' sx={{ textTransform: 'none', px: 4 }} onClick={props.onEdit}>Edit Child Information</Button>
      </Box>}
      {showLocked && <DuplicateDisclaimer pendingDuplicateID={props.pendingDuplicateID ?? 0} /> }
    </Box >
  }
}
